// Byte / base64 helpers that work identically in the browser and in Node/vitest,
// without relying on `Buffer`. The call engine is framework- *and*
// platform-independent TS, so it can only use standard Web APIs.

const B64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_LOOKUP: Int16Array = (() => {
  const t = new Int16Array(256).fill(-1);
  for (let i = 0; i < B64_ALPHABET.length; i++) t[B64_ALPHABET.charCodeAt(i)] = i;
  t['='.charCodeAt(0)] = -2;
  return t;
})();

/** Standard base64 (RFC 4648, with padding) of raw bytes. */
export function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8) | bytes[i + 2]!;
    out += B64_ALPHABET[(n >> 18) & 63]! + B64_ALPHABET[(n >> 12) & 63]! + B64_ALPHABET[(n >> 6) & 63]! + B64_ALPHABET[n & 63]!;
  }
  const rem = bytes.length - i;
  if (rem === 1) {
    const n = bytes[i]! << 16;
    out += B64_ALPHABET[(n >> 18) & 63]! + B64_ALPHABET[(n >> 12) & 63]! + '==';
  } else if (rem === 2) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8);
    out += B64_ALPHABET[(n >> 18) & 63]! + B64_ALPHABET[(n >> 12) & 63]! + B64_ALPHABET[(n >> 6) & 63]! + '=';
  }
  return out;
}

/**
 * Decode standard base64 to raw bytes. Returns `null` for input that is not
 * well-formed base64 (so callers can treat malformed key/signature material as
 * a verification failure rather than throwing).
 */
export function base64ToBytes(b64: string): Uint8Array | null {
  const clean = b64.trim();
  if (clean.length % 4 !== 0) return null;
  let bytesLen = (clean.length / 4) * 3;
  if (clean.endsWith('==')) bytesLen -= 2;
  else if (clean.endsWith('=')) bytesLen -= 1;
  const out = new Uint8Array(bytesLen);
  let o = 0;
  for (let i = 0; i < clean.length; i += 4) {
    const c0 = B64_LOOKUP[clean.charCodeAt(i)]!;
    const c1 = B64_LOOKUP[clean.charCodeAt(i + 1)]!;
    const c2 = B64_LOOKUP[clean.charCodeAt(i + 2)]!;
    const c3 = B64_LOOKUP[clean.charCodeAt(i + 3)]!;
    if (c0 < 0 || c1 < 0) return null;
    out[o++] = (c0 << 2) | (c1 >> 4);
    if (c2 === -2) break; // padding
    if (c2 < 0) return null;
    out[o++] = ((c1 & 15) << 4) | (c2 >> 2);
    if (c3 === -2) break; // padding
    if (c3 < 0) return null;
    out[o++] = ((c2 & 3) << 6) | c3;
  }
  return out;
}

/** UTF-8 encode a string to bytes. */
export function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

/** Concatenate byte arrays into one. */
export function concatBytes(...parts: Uint8Array[]): Uint8Array {
  let len = 0;
  for (const p of parts) len += p.length;
  const out = new Uint8Array(len);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

/** Constant-time-ish equality for two byte arrays. */
export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}
